import { useEffect, useMemo, useState } from "react";
import { useDataProvider, useGetList, useNotify } from "ra-core";
import {
  Archive,
  Check,
  Copy,
  ListPlus,
  Loader2,
  Mail,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import type { RevenueReport } from "./types";

type SalesRow = {
  id: number;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  disabled: boolean;
};

type TicketProposal = {
  title: string;
  description?: string;
  ticket_type?: "feature" | "bug" | "refacto" | "chore";
  priority?: "urgent" | "high" | "medium" | "low" | "none";
  env?: "prod" | "dev" | "mobile" | "api";
  requester_kind?: "client" | "user" | "internal";
  requester_name?: string;
};

// Parse le bloc ```json:ticket_proposals ... ``` du markdown produit par
// le skill revenue_strategy_report. Retourne { proposals, cleanContent }
// où cleanContent est le markdown sans le bloc JSON (pour ne pas l'afficher
// brut au lecteur).
function extractTicketProposals(content: string): {
  proposals: TicketProposal[];
  cleanContent: string;
} {
  const re = /```json:ticket_proposals\s*\n([\s\S]*?)\n```/g;
  let proposals: TicketProposal[] = [];
  const cleanContent = content.replace(re, (_, jsonStr) => {
    try {
      const parsed = JSON.parse(jsonStr);
      if (Array.isArray(parsed)) {
        proposals = parsed.filter(
          (p) => p && typeof p === "object" && typeof p.title === "string",
        );
      }
    } catch {
      // ignore parse errors, garde le bloc original masqué
    }
    return "";
  });
  return { proposals, cleanContent };
}

// Sprint 10.x Phase B — Carte d'affichage d'un rapport stratégique généré.
// Le markdown vient du skill `revenue_strategy_report` (DeepSeek) et
// contient des sections H1/H2/H3, des listes, et potentiellement des
// tables (top comptes) → remark-gfm activé pour gérer les tables.
//
// Actions :
//  - Copier markdown dans le presse-papier
//  - Envoyer par email (désactivé tant que l'edge function dédiée n'existe pas)
//  - Régénérer (callback parent qui re-déclenche streamSkillRun)
//  - Archiver (set archived_at via dataProvider.update)

interface StrategyReportCardProps {
  report: RevenueReport;
  onRegenerate: () => void;
  onArchived: () => void;
  regenerating: boolean;
}

export function StrategyReportCard({
  report,
  onRegenerate,
  onArchived,
  regenerating,
}: StrategyReportCardProps) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [archiving, setArchiving] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailPopoverOpen, setEmailPopoverOpen] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(
    new Set(),
  );
  const [createdTicketIndexes, setCreatedTicketIndexes] = useState<Set<number>>(
    new Set(),
  );
  const [creatingTicketIndex, setCreatingTicketIndex] = useState<number | null>(
    null,
  );

  const { proposals: ticketProposals, cleanContent } = useMemo(
    () => extractTicketProposals(report.content),
    [report.content],
  );

  const handleCreateTicket = async (idx: number, proposal: TicketProposal) => {
    setCreatingTicketIndex(idx);
    try {
      await dataProvider.create("dev_tasks", {
        data: {
          title: proposal.title,
          description: proposal.description ?? "",
          status: "backlog",
          priority: proposal.priority ?? "medium",
          ticket_type: proposal.ticket_type ?? "feature",
          env: proposal.env ?? "prod",
          requester_kind: proposal.requester_kind ?? "internal",
          requester_name: proposal.requester_name ?? "Rapport Stratégie",
        },
      });
      setCreatedTicketIndexes((prev) => new Set(prev).add(idx));
      notify(`Ticket créé : ${proposal.title}`, { type: "success" });
    } catch (err) {
      notify(`Création ticket échouée : ${(err as Error).message}`, {
        type: "error",
      });
    } finally {
      setCreatingTicketIndex(null);
    }
  };

  // Fetch sales actifs (founders) pour le picker recipients
  const { data: salesList } = useGetList<SalesRow>("sales", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "first_name", order: "ASC" },
    filter: { disabled: false },
  });

  // Pre-select all founders quand la popover s'ouvre la 1re fois
  useEffect(() => {
    if (emailPopoverOpen && selectedRecipients.size === 0 && salesList) {
      setSelectedRecipients(new Set(salesList.map((s) => s.user_id)));
    }
  }, [emailPopoverOpen, salesList, selectedRecipients.size]);

  const toggleRecipient = (userId: string) => {
    setSelectedRecipients((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleSendEmail = async () => {
    if (selectedRecipients.size === 0) {
      notify("Sélectionne au moins un destinataire", { type: "warning" });
      return;
    }
    setSending(true);
    try {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "send_strategy_report",
        {
          body: {
            report_id: report.id,
            recipient_user_ids: Array.from(selectedRecipients),
          },
        },
      );
      if (error) throw error;
      const result = data as {
        sent?: boolean;
        recipients?: number;
        recipient_names?: string[];
        failed?: string[];
      };
      const names = (result?.recipient_names ?? []).join(", ");
      if (result?.failed && result.failed.length > 0) {
        notify(
          `Envoyé à ${result.recipients ?? 0} destinataire(s). Échecs : ${result.failed.length}`,
          { type: "warning" },
        );
      } else {
        notify(`Rapport envoyé à ${names || "le(s) destinataire(s)"}`, {
          type: "success",
        });
      }
      setEmailPopoverOpen(false);
    } catch (err) {
      const msg = (err as { message?: string }).message ?? String(err);
      notify(`Envoi échoué : ${msg}`, { type: "error" });
    } finally {
      setSending(false);
    }
  };

  const rel = formatDistanceToNowStrict(new Date(report.generated_at), {
    addSuffix: true,
    locale: fr,
  });

  const handleCopy = () => {
    if (!navigator.clipboard) {
      notify("Presse-papier indisponible", { type: "error" });
      return;
    }
    navigator.clipboard.writeText(report.content).then(
      () => notify("Rapport copié dans le presse-papier", { type: "info" }),
      () => notify("Copie impossible", { type: "error" }),
    );
  };

  const handleArchive = async () => {
    if (
      !window.confirm(
        "Archiver ce rapport ? Il n'apparaîtra plus dans l'historique récent.",
      )
    ) {
      return;
    }
    setArchiving(true);
    try {
      await dataProvider.update("revenue_reports", {
        id: report.id,
        data: { archived_at: new Date().toISOString() },
        previousData: report,
      });
      notify("Rapport archivé", { type: "success" });
      onArchived();
    } catch (err) {
      notify(`Archivage échoué : ${(err as Error).message}`, { type: "error" });
    } finally {
      setArchiving(false);
    }
  };

  const sectorsLabel =
    report.sectors && report.sectors.length > 0
      ? report.sectors.join("/")
      : "tous secteurs";

  return (
    <Card data-testid="strategy-report-card">
      <CardContent className="p-5 md:p-6">
        {/* Header meta */}
        <div className="flex items-center gap-2 mb-4 text-[12px] text-muted-foreground flex-wrap">
          <Sparkles className="size-3.5 text-violet-600 dark:text-violet-400" />
          <span>Généré {rel}</span>
          {report.model && (
            <>
              <span aria-hidden className="text-muted-foreground/50">
                ·
              </span>
              <span>{report.model}</span>
            </>
          )}
          <span aria-hidden className="text-muted-foreground/50">
            ·
          </span>
          <span>période {report.period_days}j</span>
          <span aria-hidden className="text-muted-foreground/50">
            ·
          </span>
          <span>{sectorsLabel}</span>
        </div>

        {/* Markdown body */}
        <div
          className={cn(
            "strategy-markdown text-[14px] text-foreground/90 leading-relaxed",
            "prose-headings:font-semibold",
          )}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => (
                <h2 className="text-[18px] font-semibold text-foreground mt-5 mb-2.5 first:mt-0">
                  {children}
                </h2>
              ),
              h2: ({ children }) => (
                <h3 className="text-[16px] font-semibold text-foreground mt-4 mb-2 first:mt-0">
                  {children}
                </h3>
              ),
              h3: ({ children }) => (
                <h4 className="text-[14px] font-semibold text-foreground mt-3 mb-1.5 first:mt-0">
                  {children}
                </h4>
              ),
              p: ({ children }) => (
                <p className="my-2 first:mt-0 last:mb-0">{children}</p>
              ),
              ul: ({ children }) => (
                <ul className="my-2 space-y-1 list-disc pl-5 marker:text-violet-500/70">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="my-2 space-y-1 list-decimal pl-5">{children}</ol>
              ),
              li: ({ children }) => <li className="pl-0.5">{children}</li>,
              strong: ({ children }) => (
                <strong className="font-semibold text-foreground">
                  {children}
                </strong>
              ),
              a: ({ href, children }) => (
                <a
                  href={href}
                  className="text-violet-700 hover:underline dark:text-violet-300"
                >
                  {children}
                </a>
              ),
              code: ({ children }) => (
                <code className="rounded bg-violet-100/70 dark:bg-violet-900/40 px-1 py-0.5 text-[12px] font-mono">
                  {children}
                </code>
              ),
              table: ({ children }) => (
                <div className="my-3 overflow-x-auto">
                  <table className="w-full border-collapse text-[13px]">
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }) => (
                <thead className="bg-muted/40">{children}</thead>
              ),
              th: ({ children }) => (
                <th className="border border-border px-2.5 py-1.5 text-left font-semibold">
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="border border-border px-2.5 py-1.5 align-top">
                  {children}
                </td>
              ),
              hr: () => <hr className="my-4 border-border" />,
            }}
          >
            {cleanContent}
          </ReactMarkdown>
        </div>

        {/* Ticket proposals — extraits du bloc ticket_proposals du markdown */}
        {ticketProposals.length > 0 && (
          <div className="mt-5 pt-4 border-t">
            <div className="flex items-center gap-2 mb-3">
              <ListPlus className="size-4 text-violet-600 dark:text-violet-400" />
              <p className="text-[13px] font-semibold">
                Tickets dev proposés ({ticketProposals.length})
              </p>
              <span className="text-[11.5px] text-muted-foreground">
                à pousser dans le backlog Jules/Faycal
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {ticketProposals.map((p, idx) => {
                const created = createdTicketIndexes.has(idx);
                const creating = creatingTicketIndex === idx;
                return (
                  <div
                    key={idx}
                    className="rounded-md border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10 px-3 py-2.5"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="text-[13.5px] font-semibold">
                            {p.title}
                          </p>
                          {p.ticket_type && (
                            <span className="text-[10.5px] font-medium uppercase tracking-wider rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
                              {p.ticket_type}
                            </span>
                          )}
                          {p.priority && p.priority !== "none" && (
                            <span
                              className={cn(
                                "text-[10.5px] font-medium uppercase tracking-wider rounded px-1.5 py-0.5",
                                p.priority === "urgent" &&
                                  "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300",
                                p.priority === "high" &&
                                  "bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300",
                                p.priority === "medium" &&
                                  "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
                                p.priority === "low" &&
                                  "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300",
                              )}
                            >
                              {p.priority}
                            </span>
                          )}
                        </div>
                        {p.description && (
                          <p className="text-[12.5px] text-muted-foreground leading-relaxed">
                            {p.description}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant={created ? "ghost" : "default"}
                        className="gap-1.5 h-7 text-[11.5px] shrink-0"
                        onClick={() => handleCreateTicket(idx, p)}
                        disabled={created || creating}
                      >
                        {creating ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : created ? (
                          <Check className="size-3.5" />
                        ) : (
                          <ListPlus className="size-3.5" />
                        )}
                        {created
                          ? "Ticket créé"
                          : creating
                            ? "Création…"
                            : "Valider et créer"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 pt-4 border-t flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5 h-8 text-xs"
            onClick={handleCopy}
          >
            <Copy className="size-3.5" />
            Copier
          </Button>
          <Popover open={emailPopoverOpen} onOpenChange={setEmailPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5 h-8 text-xs"
              >
                <Mail className="size-3.5" />
                Envoyer par email
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-3">
              <p className="text-[12px] font-medium mb-2">
                Choisir les destinataires
              </p>
              <div className="flex flex-col gap-2 mb-3">
                {(salesList ?? []).map((s) => {
                  const fullName =
                    `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim() ||
                    s.email;
                  return (
                    <Label
                      key={s.user_id}
                      className="flex items-center gap-2 cursor-pointer text-[12.5px] font-normal"
                    >
                      <Checkbox
                        checked={selectedRecipients.has(s.user_id)}
                        onCheckedChange={() => toggleRecipient(s.user_id)}
                      />
                      <span className="flex-1 truncate">{fullName}</span>
                    </Label>
                  );
                })}
                {(!salesList || salesList.length === 0) && (
                  <p className="text-[11.5px] text-muted-foreground">
                    Aucun destinataire trouvé.
                  </p>
                )}
              </div>
              <Button
                type="button"
                size="sm"
                className="w-full gap-1.5 h-8 text-xs"
                onClick={handleSendEmail}
                disabled={sending || selectedRecipients.size === 0}
              >
                {sending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Mail className="size-3.5" />
                )}
                {sending ? "Envoi…" : `Envoyer (${selectedRecipients.size})`}
              </Button>
            </PopoverContent>
          </Popover>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5 h-8 text-xs"
            onClick={onRegenerate}
            disabled={regenerating}
          >
            {regenerating ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            {regenerating ? "Génération…" : "Régénérer"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="gap-1.5 h-8 text-xs ml-auto text-muted-foreground hover:text-foreground"
            onClick={handleArchive}
            disabled={archiving}
          >
            {archiving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Archive className="size-3.5" />
            )}
            Archiver
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
